/**
 * 追问前端单例 store（Vue.observable）。
 * 追问注解只存在于前端内存，发送时按约定文本协议拼进用户消息内容。
 */
import Vue from 'vue'
import { normalizeFollowUpText, getAnnotationNoteText } from './follow-ups'

const state = Vue.observable({
  sessionId: '',
  annotationsByMessage: {},
  island: {
    visible: false,
    mode: 'compact', // compact | create | edit
    messageId: '',
    annotationId: '',
    anchorX: 0,
    anchorY: 0,
    selection: null // { start, end, quote }
  },
  sendRequestId: 0
})

function messageAnnotations(messageId) {
  if (!messageId) return []
  return state.annotationsByMessage[messageId] || []
}

function setMessageAnnotations(messageId, list) {
  if (!messageId) return
  Vue.set(state.annotationsByMessage, messageId, list)
}

function addAnnotation(messageId, annotation) {
  if (!messageId || !annotation) return
  const list = messageAnnotations(messageId).slice()
  list.push(annotation)
  setMessageAnnotations(messageId, list)
}

function updateAnnotationNote(messageId, annotationId, note) {
  const list = messageAnnotations(messageId).map((annotation) => {
    if (annotation.id !== annotationId) return annotation
    const text = typeof note === 'string' ? note.trim() : ''
    const body = [{ type: 'highlight' }]
    if (text) body.push({ type: 'note', text })
    const meta = { ...(annotation.meta || {}) }
    if (text) {
      meta.followUp = {
        ...(meta.followUp || {}),
        text,
        createdAt: (meta.followUp && meta.followUp.createdAt) || new Date().toISOString()
      }
    } else {
      delete meta.followUp
    }
    return {
      ...annotation,
      intent: text ? 'comment' : 'highlight',
      body,
      meta,
      updatedAt: new Date().toISOString()
    }
  })
  setMessageAnnotations(messageId, list)
}

function removeAnnotation(messageId, annotationId) {
  setMessageAnnotations(
    messageId,
    messageAnnotations(messageId).filter((annotation) => annotation.id !== annotationId)
  )
}

function getAnnotation(messageId, annotationId) {
  return messageAnnotations(messageId).find((annotation) => annotation.id === annotationId) || null
}

/**
 * 待发送追问：跨所有助手消息，按 updatedAt/createdAt 升序编号。
 * 返回 [{ messageId, annotationId, index, quote, note, annotation }]
 */
function getPendingFollowUps() {
  const collected = []
  Object.keys(state.annotationsByMessage).forEach((messageId) => {
    messageAnnotations(messageId).forEach((annotation) => {
      const followUp = annotation.meta && annotation.meta.followUp
      if (!followUp) return
      const note = normalizeFollowUpText(getAnnotationNoteText(annotation))
      if (!note) return
      const lastSentText = normalizeFollowUpText(followUp.lastSentText || '')
      if (lastSentText && lastSentText === note) return
      collected.push({
        messageId,
        annotationId: annotation.id,
        annotation,
        quote: (annotation.target && annotation.target.quote) || '',
        note,
        sortAt: annotation.updatedAt || followUp.createdAt || ''
      })
    })
  })
  collected.sort((a, b) => String(a.sortAt).localeCompare(String(b.sortAt)))
  return collected.map((item, i) => ({ ...item, index: i + 1 }))
}

/**
 * 单条消息内的注解显示序号（1 起）。
 */
function getAnnotationIndexMap(messageId) {
  const map = {}
  messageAnnotations(messageId).forEach((annotation, i) => {
    map[annotation.id] = i + 1
  })
  return map
}

// 追问发送后清除对应注解：高亮色块与序号角标一并消失，气泡恢复干净
function clearSent(items) {
  if (!Array.isArray(items) || items.length === 0) return
  const byMessage = {}
  items.forEach((item) => {
    if (!byMessage[item.messageId]) byMessage[item.messageId] = new Set()
    byMessage[item.messageId].add(item.annotationId)
  })
  Object.keys(byMessage).forEach((messageId) => {
    setMessageAnnotations(
      messageId,
      messageAnnotations(messageId).filter((annotation) => !byMessage[messageId].has(annotation.id))
    )
  })
}

/* --------------- island --------------- */

// 悬浮 island 当前锚定的选区（非响应式，避免 Vue 观测 DOM Range）
let islandRange = null

function openCompactIsland({ messageId, anchorX, anchorY, selection, range }) {
  islandRange = range || null
  state.island = {
    visible: true,
    mode: 'compact',
    messageId,
    annotationId: '',
    anchorX,
    anchorY,
    selection
  }
}

function getIslandRange() {
  return islandRange
}

// 滚动/窗口尺寸变化时，按选区实时位置更新 island 锚点
function updateIslandAnchor({ anchorX, anchorY }) {
  if (!state.island.visible) return
  state.island = { ...state.island, anchorX, anchorY }
}

function expandIslandToCreate() {
  if (!state.island.visible) return
  state.island = { ...state.island, mode: 'create' }
}

function openEditIsland({ messageId, annotationId, anchorX, anchorY }) {
  islandRange = null
  state.island = {
    visible: true,
    mode: 'edit',
    messageId,
    annotationId,
    anchorX,
    anchorY,
    selection: null
  }
}

function closeIsland() {
  islandRange = null
  state.island = { ...state.island, visible: false }
  // 保留上一次 mode 便于过渡，visible 为 false 时组件不渲染
}

function requestSend() {
  state.sendRequestId += 1
}

function reset() {
  state.annotationsByMessage = {}
  islandRange = null
  state.island = {
    visible: false,
    mode: 'compact',
    messageId: '',
    annotationId: '',
    anchorX: 0,
    anchorY: 0,
    selection: null
  }
}

export const followUpStore = {
  state,
  messageAnnotations,
  setMessageAnnotations,
  addAnnotation,
  updateAnnotationNote,
  removeAnnotation,
  getAnnotation,
  getPendingFollowUps,
  getAnnotationIndexMap,
  clearSent,
  openCompactIsland,
  getIslandRange,
  updateIslandAnchor,
  expandIslandToCreate,
  openEditIsland,
  closeIsland,
  requestSend,
  reset
}

export default followUpStore
